/**
 * Proforma Detail Page
 *
 * View of a proforma/quotation with:
 * - Proforma info (dates, client)
 * - Line items table
 * - Totals
 * - Actions: Edit, Print, Convert to Invoice
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Printer,
  FileText,
  User,
  Receipt,
  Pencil,
  ArrowRight,
  Loader2,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  Trash2,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useBill, useCancelBill, useUpdateBillNote } from '@/hooks/useBills'
import api from '@/lib/api'
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useToast } from '@/components/ui/toaster'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

export function ProformaDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const proformaId = id && !isNaN(Number(id)) ? Number(id) : 0

  const { data: proforma, isLoading, error } = useBill(proformaId)

  const [linesExpanded, setLinesExpanded] = useState(true)
  const [noteEditing, setNoteEditing] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const updateNoteMutation = useUpdateBillNote()

  const startEditNote = () => {
    setNoteDraft(proforma?.notes ?? '')
    setNoteEditing(true)
  }

  const cancelEditNote = () => {
    setNoteEditing(false)
    setNoteDraft('')
  }

  const saveNote = async () => {
    try {
      const trimmed = noteDraft.trim()
      await updateNoteMutation.mutateAsync({
        billId: proformaId,
        notes: trimmed || null,
      })
      toast({ title: t('bills.clientNoteSaved') })
      setNoteEditing(false)
    } catch {
      toast({
        title: t('bills.clientNoteError'),
        variant: 'destructive',
      })
    }
  }

  const isExpired = !!proforma?.expiration_date && new Date(proforma.expiration_date) < new Date()

  // Convert proforma to invoice mutation
  const convertMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/bills/${proformaId}/convert-to-invoice`, {
        target_type: 'invoice',
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['proformas'] })
      queryClient.invalidateQueries({ queryKey: ['bills'] })

      if (data.stock_warnings?.length > 0) {
        toast({
          title: t('proformas.stockWarning'),
          description: data.stock_warnings.map((w: { code: string }) => w.code).join(', '),
          variant: 'destructive',
        })
      }

      navigate(`/bills/new?continue=${proformaId}`)
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('proformas.convertError'),
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useCancelBill()

  const handlePrint = () => {
    navigate(`/receipts/bill/${proformaId}`)
  }

  const handleEdit = () => {
    navigate(`/bills/new?continue=${proformaId}`)
  }

  const handleConvert = () => {
    convertMutation.mutate()
  }

  const handleDelete = () => {
    deleteMutation.mutate(proformaId, {
      onSuccess: () => {
        toast({
          title: t('proformas.proformaDeleted'),
          description: t('proformas.proformaDeletedDescription'),
        })
        navigate('/proformas')
      },
      onError: () => {
        toast({
          title: t('common.error'),
          description: t('bills.deleteError'),
          variant: 'destructive',
        })
      },
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !proforma) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Receipt className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('proformas.notFound')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {error ? t('proformas.errorLoading') : t('proformas.doesNotExist')}
        </p>
        <Button variant="outline" onClick={() => navigate('/proformas')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('proformas.backToProformas')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/proformas')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl md:text-2xl font-bold">
              PRO-{proforma.bill_number || proforma.id}
            </h1>
            <Badge variant="secondary">
              {t('proformas.quotation')}
            </Badge>
            {isExpired && (
              <Badge variant="destructive">
                {t('proformas.expired')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {t('proformas.quotation')} - {formatDate(proforma.created_at)}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button className="h-12" onClick={handleConvert} disabled={convertMutation.isPending}>
          {convertMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <ArrowRight className="h-4 w-4 mr-2" />
          )}
          {t('proformas.convertToInvoice')}
        </Button>
        <Button variant="outline" className="h-12" onClick={handleEdit}>
          <Pencil className="h-4 w-4 mr-2" />
          {t('common.edit')}
        </Button>
        <Button variant="outline" className="h-12" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t('receipts.print')}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('proformas.deleteProforma')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('proformas.deleteProformaConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('proformas.deleteProformaConfirmDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>
                {t('proformas.deleteProforma')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Proforma Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('proformas.proformaInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{t('bills.documentType')}</p>
                  <p className="font-medium">{t('proformas.quotation')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('bills.createdAt')}</p>
                  <p className="font-medium">{formatDateTime(proforma.created_at)}</p>
                </div>
                {proforma.expiration_date && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('proformas.expirationDate')}</p>
                    <p className={`font-medium ${isExpired ? 'text-destructive' : ''}`}>
                      {formatDate(proforma.expiration_date)}
                    </p>
                  </div>
                )}
                {proforma.user_name && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('bills.seller')}</p>
                    <p className="font-medium">{proforma.user_name}</p>
                  </div>
                )}
              </div>

              {/* Client note row (editable, max 60 chars) */}
              <div className="rounded-md border bg-muted/30 p-3 mt-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('bills.clientNote')}
                  </p>
                  {!noteEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={startEditNote}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      {proforma.notes ? t('common.edit') : t('common.add')}
                    </Button>
                  )}
                </div>
                {!noteEditing && (
                  <p className="text-sm break-words">
                    {proforma.notes || (
                      <span className="text-muted-foreground italic">
                        {t('bills.clientNoteEmpty')}
                      </span>
                    )}
                  </p>
                )}
                {noteEditing && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      type="text"
                      maxLength={60}
                      placeholder={t('bills.clientNotePlaceholder')}
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      className="h-9"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={saveNote}
                        disabled={updateNoteMutation.isPending}
                      >
                        {t('bills.clientNoteSave')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEditNote}
                        disabled={updateNoteMutation.isPending}
                      >
                        {t('bills.clientNoteCancel')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Client Info Card */}
          {(proforma.client_name || proforma.client_id) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('bills.clientInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-lg">{proforma.client_name}</p>
                    {proforma.client_identification && (
                      <p className="text-sm text-muted-foreground">
                        {proforma.client_id_type || 'ID'}: {proforma.client_identification}
                      </p>
                    )}
                  </div>
                  {(proforma.client_email || proforma.client_phone) && (
                    <div className="flex flex-wrap gap-2">
                      {proforma.client_email && (
                        <a
                          href={`mailto:${proforma.client_email}`}
                          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Mail className="h-3 w-3" />
                          {proforma.client_email}
                        </a>
                      )}
                      {proforma.client_phone && (
                        <a
                          href={`tel:${proforma.client_phone}`}
                          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Phone className="h-3 w-3" />
                          {proforma.client_phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Line Items */}
          <Collapsible open={linesExpanded} onOpenChange={setLinesExpanded}>
            <Card>
              <CardHeader className="pb-3">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between cursor-pointer">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Receipt className="h-4 w-4" />
                      {t('bills.lineItems')} ({proforma.lines?.length || 0})
                    </CardTitle>
                    {linesExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  {/* Mobile View */}
                  <div className="space-y-3 md:hidden">
                    {proforma.lines?.map((line) => (
                      <div key={line.id} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{line.article_description}</p>
                            <p className="text-xs text-muted-foreground">{line.article_code}</p>
                          </div>
                          <p className="font-bold ml-2">{formatCurrency(line.total)}</p>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>{line.quantity} x {formatCurrency(line.unit_price)}</span>
                          {line.discount_percent > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              -{line.discount_percent}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop View */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('bills.code')}</TableHead>
                          <TableHead>{t('bills.description')}</TableHead>
                          <TableHead className="text-right">{t('bills.quantity')}</TableHead>
                          <TableHead className="text-right">{t('bills.unitPrice')}</TableHead>
                          <TableHead className="text-right">{t('bills.discount')}</TableHead>
                          <TableHead className="text-right">{t('bills.total')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {proforma.lines?.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell className="font-mono text-sm">
                              {line.article_code}
                            </TableCell>
                            <TableCell>{line.article_description}</TableCell>
                            <TableCell className="text-right">{line.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(line.unit_price)}</TableCell>
                            <TableCell className="text-right">
                              {line.discount_percent > 0 ? `${line.discount_percent}%` : '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(line.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {(!proforma.lines || proforma.lines.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">
                      {t('bills.noLineItems')}
                    </p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* Right Column - Totals */}
        <div className="space-y-4">
          {/* Totals Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('bills.totals')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('bills.subtotal')}</span>
                <span>{formatCurrency(proforma.subtotal)}</span>
              </div>
              {proforma.total_line_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('bills.lineDiscounts')}</span>
                  <span>-{formatCurrency(proforma.total_line_discount)}</span>
                </div>
              )}
              {proforma.total_general_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('bills.generalDiscount')}</span>
                  <span>-{formatCurrency(proforma.total_general_discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('bills.tax')} (13%)</span>
                <span>{formatCurrency(proforma.total_sale_tax)}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('bills.total')}</span>
                  <span>{formatCurrency(proforma.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-sm text-muted-foreground">
                <p className="font-medium mb-2">{t('proformas.validityNote')}</p>
                <p>{t('proformas.validityDescription')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
